/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";

// react-router-dom
import { useNavigate } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import MDAvatar from "components/MDAvatar";
import MDPagination from "components/MDPagination";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

function Tables() {
  const navigate = useNavigate();
  const [users, setUsers] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [limit] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalUsersCount, setTotalUsersCount] = React.useState(0);
  const [selectedUserIds, setSelectedUserIds] = React.useState([]);
  const [bulkTitleDialog, setBulkTitleDialog] = React.useState({ open: false, title: "" });
  const [actionAnchorEl, setActionAnchorEl] = React.useState(null);
  const [selectedActionUser, setSelectedActionUser] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [showUserDialog, setShowUserDialog] = React.useState(false);
  const [isUserDialogLoading, setIsUserDialogLoading] = React.useState(false);
  const [userDialogData, setUserDialogData] = React.useState(null);

  const token =
    localStorage.getItem("dsf_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    "";
  const apiBaseUrl = (
    process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
  ).replace(/\/+$/, "");

  const authHeaders = React.useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const buildUsersListUrls = React.useCallback(
    (nextPage) => {
      const endpoint = `/users?page=${nextPage}&limit=${limit}`;
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
      return requestUrls;
    },
    [apiBaseUrl, limit]
  );

  const buildUserByIdUrls = React.useCallback(
    (id) => {
      const endpoint = `/users/${id}`;
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
      return requestUrls;
    },
    [apiBaseUrl]
  );

  const formatDate = React.useCallback((value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  }, []);

  const formatLastActive = React.useCallback((value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  }, []);

  React.useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      const requestUrls = buildUsersListUrls(page);

      try {
        let response;
        for (const url of requestUrls) {
          try {
            response = await axios.get(url, {
              headers: authHeaders,
            });
            break;
          } catch (_requestError) {
            // Try next url
          }
        }

        const payload = response?.data?.data || response?.data || {};
        const list = Array.isArray(payload.users)
          ? payload.users
          : Array.isArray(payload.docs)
          ? payload.docs
          : Array.isArray(payload.items)
          ? payload.items
          : [];

        const pagination = payload.pagination || payload.meta || {};
        const resolvedTotal =
          Number(pagination.total || pagination.totalUsers || payload.total || list.length) || 0;
        const resolvedTotalPages =
          Number(
            pagination.totalPages ||
              payload.totalPages ||
              (resolvedTotal > 0 ? Math.ceil(resolvedTotal / limit) : 1)
          ) || 1;
        const normalized = list.map((user, idx) => ({
          id: user?._id || user?.id || `${page}-${idx}`,
          image: user?.profilePhoto || "",
          name: user?.name || "User",
          email: user?.email || "—",
          role: user?.role || "User",
          joinDate: formatDate(user?.createdAt || user?.joinDate),
          lastActive: formatLastActive(user?.lastActiveAt),
          plan:
            user?.planName ||
            user?.plan ||
            user?.subscriptionPlan ||
            user?.subscription?.planName ||
            user?.subscription?.plan ||
            user?.packageName ||
            user?.package ||
            (Array.isArray(user?.tags)
              ? user.tags
                  .map((tag) =>
                    typeof tag === "string" ? tag : tag?.name || tag?.description || tag?._id || ""
                  )
                  .filter(Boolean)
                  .join(", ")
              : "") ||
            "—",
          accountStatus:
            user?.accountStatus || user?.status || (user?.isBlocked ? "blocked" : "active"),
          raw: user,
        }));

        setUsers(normalized);
        setTotalUsersCount(resolvedTotal);
        setTotalPages(resolvedTotalPages);
        setSelectedUserIds((prev) => prev.filter((id) => normalized.some((u) => u.id === id)));
      } catch (_error) {
        setUsers([]);
        setTotalUsersCount(0);
        setTotalPages(1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [authHeaders, buildUsersListUrls, formatDate, formatLastActive, page, reloadKey]);

  const allSelected = users.length > 0 && selectedUserIds.length === users.length;
  const partiallySelected = selectedUserIds.length > 0 && selectedUserIds.length < users.length;

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedUserIds((prev) => (prev.length === users.length ? [] : users.map((u) => u.id)));
  };

  const selectedUsers = React.useMemo(
    () => users.filter((user) => selectedUserIds.includes(user.id)),
    [selectedUserIds, users]
  );

  const createUsersReport = async (title = "") => {
    if (!selectedUsers.length) return false;
    const endpoint = "/reports/generate/users";
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
    const maybeTitle = String(title || "").trim();
    const payload = {
      userIds: selectedUsers.map((user) => user.id).filter(Boolean),
      ...(maybeTitle ? { title: maybeTitle } : {}),
    };

    for (const url of requestUrls) {
      try {
        const response = await axios.post(url, payload, { headers: authHeaders });
        const apiMessage = response?.data?.message || "Report generated";
        toast.success(apiMessage);
        navigate("/reports");
        return true;
      } catch (_requestError) {
        // Try next URL shape.
      }
    }

    toast.error("Unable to generate report. Please try again.");
    return false;
  };

  const generateBulkReport = async () => {
    if (!selectedUsers.length) return;
    if (selectedUsers.length === 1) {
      await createUsersReport(`${selectedUsers[0].name || "User"} report`);
      return;
    }
    setBulkTitleDialog({ open: true, title: "" });
  };

  const openUserDialog = React.useCallback(
    async (user) => {
      setShowUserDialog(true);
      setIsUserDialogLoading(true);
      setUserDialogData(null);
      const requestUrls = buildUserByIdUrls(user.id);
      let response;
      for (const url of requestUrls) {
        try {
          response = await axios.get(url, { headers: authHeaders });
          break;
        } catch (_requestError) {
          // Try next url
        }
      }
      const payload = response?.data?.data || response?.data || null;
      const resolvedUser =
        payload?.user || payload?.data || (payload && payload._id ? payload : null);
      setUserDialogData(resolvedUser || user.raw || user);
      setIsUserDialogLoading(false);
    },
    [authHeaders, buildUserByIdUrls]
  );

  const columns = React.useMemo(
    () => [
      { Header: "select", accessor: "select", align: "center", width: "6%" },
      { Header: "user", accessor: "user", align: "left", width: "34%" },
      { Header: "join date", accessor: "joinDate", align: "left" },
      { Header: "last active", accessor: "lastActive", align: "center" },
      { Header: "tags", accessor: "plan", align: "center" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "action", accessor: "action", align: "center" },
    ],
    []
  );

  const rows = React.useMemo(
    () =>
      users.map((user) => ({
        select: (
          <MDBox display="flex" justifyContent="center">
            <Checkbox
              size="small"
              checked={selectedUserIds.includes(user.id)}
              onChange={() => toggleUserSelection(user.id)}
            />
          </MDBox>
        ),
        user: (
          <MDBox
            display="flex"
            alignItems="center"
            lineHeight={1}
            onClick={() => openUserDialog(user)}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            <MDAvatar src={user.image} name={user.name} size="sm" />
            <MDBox ml={2} lineHeight={1}>
              <MDTypography display="block" variant="button" fontWeight="medium">
                {user.name}
              </MDTypography>
              <MDTypography variant="caption">{user.email}</MDTypography>
            </MDBox>
          </MDBox>
        ),
        joinDate: (
          <MDBox lineHeight={1} textAlign="left">
            <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
              Join Date
            </MDTypography>
            <MDTypography variant="caption">{user.joinDate}</MDTypography>
          </MDBox>
        ),
        lastActive: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {user.lastActive}
          </MDTypography>
        ),
        plan: (
          <MDBox display="flex" alignItems="center" justifyContent="center">
            <MDBox
              px={1}
              py={0.35}
              borderRadius="md"
              sx={{
                backgroundColor: "rgba(17, 24, 39, 0.06)",
                color: "#344767",
                fontSize: "0.75rem",
                lineHeight: 1.2,
                minWidth: "3rem",
                textAlign: "center",
              }}
            >
              {user.plan || "—"}
            </MDBox>
          </MDBox>
        ),
        status: (
          <MDBox display="flex" justifyContent="center">
            <MDBadge
              badgeContent={user.accountStatus}
              color={user.accountStatus === "active" ? "success" : "warning"}
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ),
        action: (
          <MDBox display="flex" justifyContent="center">
            <IconButton
              size="small"
              sx={{ color: "#344767" }}
              onClick={(event) => {
                setActionAnchorEl(event.currentTarget);
                setSelectedActionUser(user);
              }}
            >
              <Icon fontSize="small">more_vert</Icon>
            </IconButton>
          </MDBox>
        ),
      })),
    [openUserDialog, selectedUserIds, users]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="contained"
                borderRadius="lg"
                coloredShadow="none"
                sx={{ background: "rgba(229, 10, 70, 1)" }}
              >
                <MDTypography variant="h6" color="white">
                  Users
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <MDBox
                  px={2}
                  pb={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                >
                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" color="text">
                      Bulk Operations:
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block">
                      {selectedUserIds.length} selected of {totalUsersCount || users.length}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={partiallySelected}
                      onChange={toggleSelectAll}
                    />
                    <MDButton
                      variant="gradient"
                      color="info"
                      disabled={!selectedUserIds.length}
                      onClick={generateBulkReport}
                      sx={{
                        background: "rgba(229, 10, 70, 1)",
                        "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
                      }}
                    >
                      Generate Bulk Report
                    </MDButton>
                  </MDBox>
                </MDBox>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
                {isLoading ? (
                  <MDBox py={2} display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <CircularProgress size={18} sx={{ color: "rgba(229, 10, 70, 1)" }} />
                    <MDTypography variant="caption" color="text">
                      Loading users...
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox
                    px={2}
                    pt={1.5}
                    pb={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <MDBox display="flex" flexDirection="column">
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        Page {page} of {totalPages} ({totalUsersCount} users)
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Showing up to {limit} users per page
                      </MDTypography>
                    </MDBox>
                    <MDPagination
                      variant="gradient"
                      color="dark"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          color: "rgba(229, 10, 70, 1)",
                          borderColor: "rgba(229, 10, 70, 0.35)",
                        },
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: "rgba(229, 10, 70, 1) !important",
                          color: "#fff !important",
                        },
                      }}
                    >
                      <MDPagination item disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </MDPagination>
                      {Array.from({ length: totalPages }, (_, index) => index + 1)
                        .slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5)
                        .map((pageNumber) => (
                          <MDPagination
                            key={pageNumber}
                            item
                            active={pageNumber === page}
                            onClick={() => setPage(pageNumber)}
                          >
                            {pageNumber}
                          </MDPagination>
                        ))}
                      <MDPagination
                        item
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </MDPagination>
                    </MDPagination>
                  </MDBox>
                )}
                <Menu
                  anchorEl={actionAnchorEl}
                  open={Boolean(actionAnchorEl)}
                  onClose={() => {
                    setActionAnchorEl(null);
                    setSelectedActionUser(null);
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  <MenuItem
                    onClick={async () => {
                      if (!selectedActionUser) return;
                      await openUserDialog(selectedActionUser);
                      setActionAnchorEl(null);
                      setSelectedActionUser(null);
                    }}
                  >
                    View Details
                  </MenuItem>
                  <MenuItem
                    onClick={async () => {
                      if (!selectedActionUser) return;
                      const requestUrls = buildUserByIdUrls(selectedActionUser.id);
                      const nextStatus =
                        selectedActionUser.accountStatus === "blocked" ? "active" : "blocked";
                      const confirmMessage =
                        nextStatus === "blocked"
                          ? "Do you want to block this user?"
                          : "Do you want to unblock this user?";
                      if (!window.confirm(confirmMessage)) return;

                      let isUpdated = false;
                      for (const url of requestUrls) {
                        try {
                          await axios.patch(
                            url,
                            {
                              status: nextStatus,
                              accountStatus: nextStatus,
                              isBlocked: nextStatus === "blocked",
                            },
                            { headers: authHeaders }
                          );
                          isUpdated = true;
                          break;
                        } catch (_requestError) {
                          // Try next url
                        }
                      }
                      if (isUpdated) {
                        setReloadKey((prev) => prev + 1);
                        toast.success(
                          nextStatus === "blocked"
                            ? "User blocked successfully."
                            : "User unblocked successfully."
                        );
                      } else {
                        toast.error(
                          nextStatus === "blocked"
                            ? "Failed to block user."
                            : "Failed to unblock user."
                        );
                      }
                      setActionAnchorEl(null);
                      setSelectedActionUser(null);
                    }}
                  >
                    {selectedActionUser?.accountStatus === "blocked"
                      ? "Activate account"
                      : "Block account"}
                  </MenuItem>
                  <MenuItem
                    sx={{ color: "error.main" }}
                    onClick={async () => {
                      if (!selectedActionUser) return;
                      if (!window.confirm("Do you want to delete this user?")) return;
                      const requestUrls = buildUserByIdUrls(selectedActionUser.id);
                      let isDeleted = false;
                      for (const url of requestUrls) {
                        try {
                          await axios.delete(url, { headers: authHeaders });
                          isDeleted = true;
                          break;
                        } catch (_requestError) {
                          // Try next url
                        }
                      }
                      if (isDeleted) {
                        setSelectedUserIds((prev) =>
                          prev.filter((userId) => userId !== selectedActionUser.id)
                        );
                        setReloadKey((prev) => prev + 1);
                        toast.success("User deleted successfully.");
                      } else {
                        toast.error("Failed to delete user.");
                      }
                      setActionAnchorEl(null);
                      setSelectedActionUser(null);
                    }}
                  >
                    Delete account
                  </MenuItem>
                </Menu>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Dialog
        open={showUserDialog}
        onClose={() => {
          setShowUserDialog(false);
          setUserDialogData(null);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "14px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 2,
            px: 2.5,
            background: "rgba(229, 10, 70, 1)",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          User Details
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pt: 3, pb: 2.5 }}>
          {isUserDialogLoading ? (
            <MDBox py={2} display="flex" alignItems="center" justifyContent="center" gap={1}>
              <CircularProgress size={18} sx={{ color: "rgba(229, 10, 70, 1)" }} />
              <MDTypography variant="caption" color="text">
                Loading user details...
              </MDTypography>
            </MDBox>
          ) : userDialogData ? (
            <MDBox>
              <MDBox
                p={1.5}
                mt={0.5}
                mb={1.5}
                borderRadius="lg"
                sx={{
                  backgroundColor: "rgba(229, 10, 70, 0.08)",
                  border: "1px solid rgba(229, 10, 70, 0.2)",
                }}
              >
                <MDTypography variant="button" fontWeight="bold" display="block">
                  {userDialogData.name || "User"}
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block">
                  {userDialogData.email || "—"}
                </MDTypography>
              </MDBox>
              <MDBox
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                gap={1.25}
                mb={1.5}
              >
                <MDBox
                  p={1.25}
                  borderRadius="md"
                  sx={{ backgroundColor: "rgba(52, 71, 103, 0.05)" }}
                >
                  <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
                    Role
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {userDialogData.role || "User"}
                  </MDTypography>
                </MDBox>
                <MDBox
                  p={1.25}
                  borderRadius="md"
                  sx={{ backgroundColor: "rgba(52, 71, 103, 0.05)" }}
                >
                  <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
                    Status
                  </MDTypography>
                  <MDBox mt={0.25}>
                    <MDBadge
                      badgeContent={
                        userDialogData.accountStatus ||
                        userDialogData.status ||
                        (userDialogData.isBlocked ? "blocked" : "active")
                      }
                      color={
                        String(
                          userDialogData.accountStatus ||
                            userDialogData.status ||
                            (userDialogData.isBlocked ? "blocked" : "active")
                        ).toLowerCase() === "active"
                          ? "success"
                          : "warning"
                      }
                      variant="gradient"
                      size="sm"
                    />
                  </MDBox>
                </MDBox>
                <MDBox
                  p={1.25}
                  borderRadius="md"
                  sx={{ backgroundColor: "rgba(52, 71, 103, 0.05)" }}
                >
                  <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
                    Joined
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {formatDate(userDialogData.createdAt || userDialogData.joinDate)}
                  </MDTypography>
                </MDBox>
                <MDBox
                  p={1.25}
                  borderRadius="md"
                  sx={{ backgroundColor: "rgba(52, 71, 103, 0.05)" }}
                >
                  <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
                    Last Active
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {formatLastActive(userDialogData.lastActiveAt)}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                display="block"
                mb={0.5}
              >
                Plan
              </MDTypography>
              <MDBox
                p={1.25}
                borderRadius="md"
                display="flex"
                alignItems="center"
                gap={0.75}
                flexWrap="wrap"
                sx={{ backgroundColor: "rgba(52, 71, 103, 0.05)" }}
              >
                {(() => {
                  const resolvedPlan =
                    userDialogData.planName ||
                    userDialogData.plan ||
                    userDialogData.subscriptionPlan ||
                    userDialogData.subscription?.planName ||
                    userDialogData.subscription?.plan ||
                    userDialogData.packageName ||
                    userDialogData.package ||
                    (Array.isArray(userDialogData.tags)
                      ? userDialogData.tags
                          .map((tag) =>
                            typeof tag === "string"
                              ? tag
                              : tag?.name || tag?.description || tag?._id || ""
                          )
                          .filter(Boolean)
                          .join(", ")
                      : "");

                  return resolvedPlan ? (
                    <MDBadge
                      badgeContent={resolvedPlan}
                      color="dark"
                      variant="gradient"
                      size="sm"
                    />
                  ) : null;
                })()}
                {!(
                  userDialogData.planName ||
                  userDialogData.plan ||
                  userDialogData.subscriptionPlan ||
                  userDialogData.subscription?.planName ||
                  userDialogData.subscription?.plan ||
                  userDialogData.packageName ||
                  userDialogData.package ||
                  (Array.isArray(userDialogData.tags) &&
                    userDialogData.tags
                      .map((tag) =>
                        typeof tag === "string"
                          ? tag
                          : tag?.name || tag?.description || tag?._id || ""
                      )
                      .filter(Boolean)
                      .join(", "))
                ) ? (
                  <MDTypography variant="caption" color="text">
                    No plan
                  </MDTypography>
                ) : null}
              </MDBox>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text">
              User data not found.
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => {
              setShowUserDialog(false);
              setUserDialogData(null);
            }}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={bulkTitleDialog.open}
        onClose={() => setBulkTitleDialog({ open: false, title: "" })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Report Title</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <TextField
              label="Enter report title"
              fullWidth
              value={bulkTitleDialog.title}
              onChange={(event) =>
                setBulkTitleDialog((prev) => ({ ...prev, title: event.target.value }))
              }
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="text"
            color="dark"
            onClick={() => setBulkTitleDialog({ open: false, title: "" })}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={async () => {
              const nextTitle = bulkTitleDialog.title.trim();
              if (!nextTitle) {
                toast.error("Please enter report title.");
                return;
              }
              const isCreated = await createUsersReport(nextTitle);
              if (isCreated) setBulkTitleDialog({ open: false, title: "" });
            }}
            sx={{
              background: "rgba(229, 10, 70, 1)",
              "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
            }}
          >
            Generate
          </MDButton>
        </DialogActions>
      </Dialog>
      <ToastContainer position="top-right" autoClose={3000} />
    </DashboardLayout>
  );
}

export default Tables;
